import { useEffect, useRef, useState } from "react"
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query"
import { useNavigate, useParams } from "react-router-dom"
import { useAuth } from "../../../context/auth-user.jsx"
import axios from "axios"
import Swal from "sweetalert2"

const URI_CLIENT = process.env.REACT_APP_URI_CLIENT
const URI_CLIENT_BY_URL = process.env.REACT_APP_URI_CLIENT_BY_URL
const URI_INDUSTRY = process.env.REACT_APP_URI_INDUSTRIES
const URI_DISABLE_CLIENT = process.env.REACT_APP_URI_DISABLE_CLIENT

// ─── API FUNCTIONS ────────────────────────────────────────────────────────────

const fetchClientById = (id, rol) =>
    axios.post(URI_CLIENT + id, { rol }).then(res => res.data)

const fetchIndustries = () =>
    axios.get(URI_INDUSTRY).then(res => res.data)

// ─── HOOK ─────────────────────────────────────────────────────────────────────

export function useEditClient() {
    const { id } = useParams()
    const { user } = useAuth()
    const userLogged = user?.selectedProfile
    const darkLightMode = userLogged?.darkLightMode
    const navigate = useNavigate()
    const queryClient = useQueryClient()

    // DRAFT STATE: lo que el usuario edita localmente
    const [draft, setDraft] = useState(null)
    const initialized = useRef(false) // evita sobreescribir el draft en refetches

    // ENTITY MODAL STATE
    const [addModalShow, setAddModalShow] = useState(false)
    const [editModalShow, setEditModalShow] = useState(false)
    const [entityToEdit, setEntityToEdit] = useState(null)
    const [entityIdToEdit, setEntityIdToEdit] = useState(null)

    // USER MODAL STATE
    const [usersControlModal, setUsersControlModal] = useState(false)
    const [showMassiveUploadModal, setShowMassiveUploadModal] = useState(false)
    const [showUserModal, setShowUserModal] = useState(false)
    const [clientToAddUser, setClientToAddUser] = useState(null)
    const [isAdminClient, setIsAdminClient] = useState(false)
    const [havePurchasing, setHavePurchasing] = useState(false)

    // MENU REF
    const moreDotsRef = useRef(null)

    // ─── SERVER STATE ─────────────────────────────────────────────────────────

    const { data: serverClient, isLoading: isLoadingClient } = useQuery({
        queryKey: ["client", id],
        queryFn: () => fetchClientById(id, userLogged?.rol),
        enabled: !!id && !!userLogged,
    })

    const { data: industries, isLoading: isLoadingIndustries } = useQuery({
        queryKey: ["industries"],
        queryFn: fetchIndustries,
    })

    // ─── SYNC SERVER → DRAFT (solo la primera vez) ────────────────────────────

    useEffect(() => {
        if (serverClient && !initialized.current) {
            setDraft(serverClient)
            initialized.current = true
        }
    }, [serverClient])

    // ─── MENU CLOSE ───────────────────────────────────────────────────────────

    const closeMenus = (e) => {
        let menuOptions

        if (moreDotsRef.current && moreDotsRef.current.contains(e.target)) {
            menuOptions = Array.prototype.slice.call(document.getElementsByClassName("menuOptions"))
            menuOptions.forEach(element => {
                if (!moreDotsRef.current.nextElementSibling.contains(element)) {
                    element.classList.remove("show")
                }
            })
            moreDotsRef.current.nextElementSibling.classList.toggle("show")
        } else {
            menuOptions = Array.prototype.slice.call(document.getElementsByClassName("menuOptions"))
            menuOptions.forEach(element => element.classList.remove("show"))
        }
    }

    useEffect(() => {
        document.addEventListener("click", closeMenus)
        return () => document.removeEventListener("click", closeMenus)
    }, [])

    // ─── DRAFT HANDLERS ───────────────────────────────────────────────────────

    const handleClientChange = (parentProperty, property, value) => {
        setDraft(prev => {
            const copy = structuredClone(prev)
            copy[parentProperty][property] = value
            if (property === "clientCountry") {
                copy[parentProperty].clientTypePerson = ""
                copy[parentProperty].clientRfc = ""
                copy[parentProperty].clientState = ""
            }
            return copy
        })
    }

    const handleContactInformationChange = (contactId, property, value) => {
        setDraft(prev => {
            const copy = structuredClone(prev)
            const contact = copy.contacts.find(c => c._id === contactId)
            if (contact) contact[property] = value
            return copy
        })
    }

    const handleAddContact = () => {
        setDraft(prev => {
            const copy = structuredClone(prev)
            copy.contacts.push({ name: "", email: "", phone: "" })
            return copy
        })
    }

    const handleDeleteContact = (contactId) => {
        setDraft(prev => {
            const copy = structuredClone(prev)
            const idx = copy.contacts.findIndex(c => c._id === contactId)
            if (idx !== -1) copy.contacts.splice(idx, 1)
            return copy
        })
    }

    const handleDeleteEntities = (entityId) => {
        setDraft(prev => {
            const copy = structuredClone(prev)
            const idx = copy.entities.findIndex(e => e._id === entityId)
            if (idx !== -1) copy.entities.splice(idx, 1)
            return copy
        })
    }

    const handleShowEditModal = (entity) => {
        setEditModalShow(true)
        setEntityIdToEdit(entity._id)
        setEntityToEdit(entity)
    }

    const handleEditEntity = (entity) => {
        setDraft(prev => {
            const copy = structuredClone(prev)
            const idx = copy.entities.findIndex(e => e._id === entityIdToEdit)
            if (idx !== -1) copy.entities[idx] = entity
            return copy
        })
    }

    const addCategory = (target) => {
        setDraft(prev => {
            const copy = structuredClone(prev)
            const industries = copy.generalInformation.clientIndustries
            const value = target.value.toUpperCase()
            const idx = industries.findIndex(v => v === value)
            if (idx !== -1) industries.splice(idx, 1)
            else industries.push(value)
            return copy
        })
    }

    const addPurchasedModules = (target) => {
        setDraft(prev => {
            const copy = structuredClone(prev)
            const modules = copy.generalInformation.clientActiveModules
            const value = target.value.toUpperCase()
            const idx = modules.findIndex(v => v === value)
            if (idx !== -1) modules.splice(idx, 1)
            else modules.push(value)
            return copy
        })
    }

    const addPaymentWay = (target) => {
        setDraft(prev => {
            const copy = structuredClone(prev)
            const ways = copy.billingInformation.clientPaymentWay
            const value = target.value.toUpperCase()
            const idx = ways.findIndex(v => v === value)
            if (idx !== -1) ways.splice(idx, 1)
            else ways.push(value)
            return copy
        })
    }

    const addCurrency = (target) => {
        setDraft(prev => {
            const copy = structuredClone(prev)
            const currencies = copy.billingInformation.clientCurrencies
            const value = target.value.toUpperCase()
            const idx = currencies.findIndex(v => v === value)
            if (idx !== -1) currencies.splice(idx, 1)
            else currencies.push(value)
            return copy
        })
    }

    // Usado por AddEntityModal para agregar una nueva entidad al draft
    const handleSetEntities = (updaterOrValue) => {
        setDraft(prev => {
            const copy = structuredClone(prev)
            copy.entities = typeof updaterOrValue === "function"
                ? updaterOrValue(copy.entities)
                : updaterOrValue
            return copy
        })
    }

    const handleAddUserModal = (clientToAdd, checkIsAdminClient) => {
        const clientHavePurchasing = draft?.generalInformation?.clientActiveModules?.includes("PURCHASING")
        setShowUserModal(true)
        setIsAdminClient(checkIsAdminClient)
        setHavePurchasing(clientHavePurchasing)
        setClientToAddUser(clientToAdd)
    }

    // ─── CHECK URL NAME ───────────────────────────────────────────────────────

    const checkEntitiesUrlName = async (urlNameToCheck) => {
        const localEntityUrlFound = draft.entities.some(entity => entity.entityUrlName === urlNameToCheck)
        const databaseClientFound = await axios.post(URI_CLIENT_BY_URL, { clientUrl: urlNameToCheck })
        return localEntityUrlFound || databaseClientFound.data ? true : false
    }

    // ─── MUTATIONS ────────────────────────────────────────────────────────────

    const { mutate: saveClient, isPending: isSaving } = useMutation({
        mutationFn: () => axios.put(URI_CLIENT + id, { client: draft }),
        onMutate: () => {
            Swal.fire({
                footer: "Loading, please wait",
                showConfirmButton: false,
                allowOutsideClick: false,
                allowEscapeKey: false,
                didOpen: () => Swal.showLoading(),
            })
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ["client", id] })
            Swal.fire({
                icon: "success",
                text: "Client updated successfully",
                showConfirmButton: false,
                timer: 5000,
                timerProgressBar: true,
            })
        },
        onError: (err) => {
            console.log("Error al actualizar la informacion del cliente: " + err)
            Swal.fire({
                icon: "error",
                text: "Failed to update the client",
                showConfirmButton: false,
                timer: 5000,
                timerProgressBar: true,
            })
        },
    })

    const { mutate: toggleActiveClient } = useMutation({
        mutationFn: () => {
            const idsToDisable = {
                newStatus: !draft.generalInformation.clientActive,
                clientID: draft._id,
                entitiesIds: draft.entities.map(entity => entity._id),
            }
            return axios.post(URI_DISABLE_CLIENT, { idsToDisable })
        },
        onSuccess: () => {
            setDraft(prev => {
                const copy = structuredClone(prev)
                copy.generalInformation.clientActive = !copy.generalInformation.clientActive
                return copy
            })
            Swal.fire({
                icon: "success",
                text: "Client updated successfully",
                showConfirmButton: false,
                timer: 5000,
                timerProgressBar: true,
            })
        },
        onError: (err) => {
            console.log("Error al actualizar la informacion del cliente: " + err)
            Swal.fire({
                icon: "error",
                text: "Failed to update the client",
                showConfirmButton: false,
                timer: 5000,
                timerProgressBar: true,
            })
        },
    })

    const { mutate: deleteClient } = useMutation({
        mutationFn: () => axios.delete(URI_CLIENT + id),
        onMutate: () => {
            Swal.fire({
                footer: "Loading, please wait",
                showConfirmButton: false,
                allowOutsideClick: false,
                allowEscapeKey: false,
                didOpen: () => Swal.showLoading(),
            })
        },
        onSuccess: () => {
            Swal.fire({
                icon: "success",
                text: "Client deleted successfully",
                showConfirmButton: false,
                timer: 5000,
                timerProgressBar: true,
            })
            navigate(-1)
        },
        onError: (err) => {
            console.log("Error al eliminar el cliente: " + err)
            Swal.fire({
                icon: "error",
                text: "Failed to delete the client",
                showConfirmButton: false,
                timer: 5000,
                timerProgressBar: true,
            })
        },
    })

    const handleEditClient = () => {
        if (draft.entities.some(entity => !entity.entityUrlName)) {
            Swal.fire({
                icon: "error",
                text: "Please fill out the urlName field from the entities",
                showConfirmButton: false,
                timer: 5000,
                timerProgressBar: true,
            })
            return
        }
        saveClient()
    }

    const handleDeleteClient = () => {
        Swal.fire({
            title: "Are you sure you want to delete this client?",
            text: "You won't be able to revert this!",
            icon: "warning",
            showCancelButton: true,
            reverseButtons: true,
            confirmButtonText: "Yes, delete",
        }).then(result => {
            if (result.isConfirmed) deleteClient()
        })
    }

    // ─── RETURN ───────────────────────────────────────────────────────────────

    return {
        darkLightMode,
        
        // data
        draft,
        industries,
        isLoading: isLoadingClient || isLoadingIndustries,
        isSaving,
        // refs
        moreDotsRef,
        // entity modal state
        addModalShow, setAddModalShow,
        editModalShow, setEditModalShow,
        entityToEdit,
        entityIdToEdit,
        // user modal state
        usersControlModal, setUsersControlModal,
        showMassiveUploadModal, setShowMassiveUploadModal,
        showUserModal, setShowUserModal,
        clientToAddUser,
        isAdminClient,
        havePurchasing,
        // handlers
        handleClientChange,
        handleContactInformationChange,
        handleAddContact,
        handleDeleteContact,
        handleDeleteEntities,
        handleShowEditModal,
        handleEditEntity,
        addCategory,
        addPurchasedModules,
        addPaymentWay,
        addCurrency,
        handleAddUserModal,
        handleSetEntities,
        checkEntitiesUrlName,
        handleEditClient,
        handleDeleteClient,
        toggleActiveClient,
        navigate,
    }
}
